import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LicenseKey, LicenseSkillAssignment } from '../types';
import { toDateInputValue } from '../utils/license-utils';
import SkillSelectionPanel from '@/features/license-keys/components/SkillSelectionPanel';

interface LicenseEditDialogProps {
  open: boolean;
  license: LicenseKey | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onUpdate: (id: number, payload: {
    max_activations: number;
    expires_at: string | null;
    notes: string | null;
    is_active: boolean;
  }) => Promise<void>;
  onAssociateSkills: (licenseKey: string, skillIds: string[]) => Promise<void>;
  availableSkills: LicenseSkillAssignment[];
  selectedSkills: string[];
  loadingSkills: boolean;
  onToggleSkill: (skillId: string, checked: boolean) => void;
  onRefresh: () => void;
}

/**
 * 许可证编辑对话框
 */
export default function LicenseEditDialog({
  open,
  license,
  onClose,
  onSuccess,
  onError,
  onUpdate,
  onAssociateSkills,
  availableSkills,
  selectedSkills,
  loadingSkills,
  onToggleSkill,
  onRefresh,
}: LicenseEditDialogProps) {
  const { t } = useTranslation();

  const [maxActivations, setMaxActivations] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 当许可证数据变化时更新表单
  useEffect(() => {
    if (license) {
      setMaxActivations(license.max_activations);
      setExpiresAt(toDateInputValue(license.expires_at));
      setNotes(license.notes || '');
      setIsActive(license.is_active);
      setError('');
    }
  }, [license]);

  const handleSubmit = async () => {
    setError('');

    if (!license) {
      return;
    }

    const payload = {
      max_activations: maxActivations,
      expires_at: expiresAt || null,
      notes: notes || null,
      is_active: isActive,
    };

    setLoading(true);
    try {
      await onUpdate(license.id, payload);

      try {
        await onAssociateSkills(license.key, selectedSkills);
      } catch (skillsError) {
        console.error('Failed to update license skills:', skillsError);
      }

      onSuccess(t('licenses.updatedSuccess'));
      onClose();
      onRefresh();
    } catch (requestError: any) {
      const errorMessage = requestError.response?.data?.error || requestError.message || t('licenses.updateFailed');
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('licenses.editTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth
            label={t('licenses.licenseKey')}
            value={license?.key || ''}
            disabled
            helperText={t('licenses.licenseKeyCannotChange')}
          />

          <TextField
            fullWidth
            type="number"
            label={t('licenses.maxActivations')}
            value={maxActivations}
            onChange={(event) => setMaxActivations(parseInt(event.target.value, 10) || 1)}
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth
            type="date"
            label={t('licenses.expirationDate')}
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('licenses.expirationDateHint')}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('licenses.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label={t('licenses.active')}
          />

          <Divider sx={{ my: 1 }} />

          <SkillSelectionPanel
            title="Enabled Skills"
            description="Update the skills that this license can use."
            skills={availableSkills}
            selectedSkillIds={selectedSkills}
            loading={loadingSkills}
            onToggleSkill={onToggleSkill}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('licenses.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('licenses.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
